// App-wide toast notifications (UI audit, Oct 12 2026) — replaces window.alert().
//
// A tiny module-level store rather than a React context, so it can be called
// from anywhere: event handlers, catch blocks, and plain helpers in utils/
// that have no hook access (reportPartialUpload, alertUploadFailures, …).
// <Toaster /> (mounted once in Layout) subscribes and renders.
//
// Why toasts live in the shell: a form's host often closes its panel right
// after a save, so a banner inside the panel unmounts before anyone reads it.
// The shell outlives every panel and every route change.
//
// Timing: success/info auto-dismiss (4s); errors and warnings stay longer
// (9s) and can be closed — an error you can't finish reading is no error
// message at all.

let nextId = 1;
let toasts = [];
const listeners = new Set();

function emit() {
  for (const l of listeners) l(toasts);
}

export function subscribeToasts(listener) {
  listeners.add(listener);
  listener(toasts);
  return () => listeners.delete(listener);
}

export function dismissToast(id) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

const DURATION = { success: 4000, info: 4000, warning: 9000, error: 9000 };
const MAX_VISIBLE = 4;

function push(kind, message) {
  if (!message) return;
  const id = nextId++;
  toasts = [...toasts, { id, kind, message: String(message) }].slice(-MAX_VISIBLE);
  emit();
  setTimeout(() => dismissToast(id), DURATION[kind]);
  return id;
}

export const toast = {
  success: (m) => push("success", m),
  info: (m) => push("info", m),
  warning: (m) => push("warning", m),
  error: (m) => push("error", m),
};

/**
 * Readable text for a failed API call. FastAPI's 422 `detail` is an array of
 * {loc, msg}; string-concatenating it printed "[object Object]".
 */
export function apiErrorMessage(err, prefix) {
  const detail = err?.response?.data?.detail;
  let text;
  if (Array.isArray(detail)) {
    text = detail
      .map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
        return field ? `${field}: ${d?.msg ?? ""}` : d?.msg ?? String(d);
      })
      .join("; ");
  } else if (detail && typeof detail === "object") {
    text = detail.message ?? JSON.stringify(detail);
  } else {
    text = detail ?? err?.message ?? "Something went wrong";
  }
  return prefix ? `${prefix}: ${text}` : text;
}
